import * as React from 'react';
import { FC, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate, MenuItemLink } from 'react-admin';
import { makeStyles } from '@material-ui/core/styles';
import { AnimatePresence, motion } from 'framer-motion';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import grid from '../grid';
import { AppState } from '../types';
import {
    resolveLabel,
    SidebarChild,
    SidebarGroup,
    sortLinks,
    TranslateFn,
} from './sidebarConfig';

interface SubMenuProps {
    dense: boolean;
    onMenuClick: () => void;
    groups: SidebarGroup[];
}

interface SidebarCategoryProps {
    group: SidebarGroup;
    dense: boolean;
    onMenuClick: () => void;
    sidebarOpen: boolean;
    translate: TranslateFn;
    nested?: boolean;
}

const useStyles = makeStyles((theme) => {
    const isDark = theme.palette.type === 'dark';
    const treeLine = isDark ? 'rgba(255, 255, 255, 0.16)' : 'rgba(28, 30, 31, 0.12)';
    return {
    categoryRoot: {
        margin: '4px 8px 8px',
    },
    nestedRoot: {
        margin: 0,
    },
    parentButton: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        padding: '10px 14px',
        borderRadius: 999,
        background: 'transparent',
        color: isDark ? '#d3dadd' : '#3f4a4e',
        transition: 'background 160ms ease, color 160ms ease, box-shadow 160ms ease',
        outline: 'none',
        fontFamily: 'inherit',
        '&:hover': {
            background: isDark ? 'rgba(218, 41, 114, 0.18)' : 'rgba(218, 41, 114, 0.08)',
        },
    },
    nestedButton: {
        padding: '8px 12px',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(28, 30, 31, 0.1)'}`,
        background: isDark ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
        '& $parentIcon': {
            minWidth: 34,
            marginRight: 0,
            justifyContent: 'flex-start',
            color: isDark ? '#9aa8ac' : '#5b6a86',
        },
        '& $parentLabel': {
            fontSize: '1rem',
            fontWeight: 400,
        },
    },
    parentButtonOpen: {
        background: '#DA2972',
        color: '#ffffff',
        boxShadow: '0 8px 18px rgba(218, 41, 114, 0.28)',
        '&:hover': {
            background: '#C32266',
        },
        '& $parentIcon': {
            color: 'inherit',
        },
    },
    parentIcon: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 10,
        color: 'inherit',
        '& .MuiSvgIcon-root': {
            fontSize: 20,
            color: 'inherit',
        },
    },
    parentLabel: {
        flex: 1,
        minWidth: 0,
        fontSize: '0.88rem',
        fontWeight: 600,
        lineHeight: 1.3,
    },
    chevron: {
        display: 'flex',
        marginLeft: 6,
        color: 'inherit',
        '& .MuiSvgIcon-root': {
            fontSize: 20,
            color: 'inherit',
        },
    },
    tree: {
        position: 'relative',
        margin: '6px 0 4px 22px',
        paddingLeft: 14,
        '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 12,
            width: 2,
            borderRadius: 2,
            background: treeLine,
        },
    },
    treeItem: {
        position: 'relative',
        marginBottom: 6,
        '&::before': {
            content: '""',
            position: 'absolute',
            left: -14,
            top: '50%',
            width: 12,
            height: 2,
            background: treeLine,
        },
    },
    childLink: {
        margin: '0 !important',
        borderRadius: '999px !important',
        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(28, 30, 31, 0.1)'} !important`,
        borderLeft: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(28, 30, 31, 0.1)'} !important`,
        background: `${isDark ? 'rgba(255, 255, 255, 0.04)' : '#ffffff'} !important`,
        padding: '8px 12px !important',
        color: `${isDark ? '#d3dadd' : '#3f4a4e'} !important`,
        boxShadow: 'none !important',
        transition: 'all 160ms ease !important',
        '&:hover': {
            background: `${isDark ? 'rgba(218, 41, 114, 0.16)' : 'rgba(218, 41, 114, 0.06)'} !important`,
            borderColor: 'rgba(218, 41, 114, 0.25) !important',
        },
        '& .MuiListItemIcon-root': {
            color: `${isDark ? '#9aa8ac' : '#5b6a86'} !important`,
            minWidth: 34,
        },
        '&[aria-current="page"]': {
            background: '#DA2972 !important',
            color: '#ffffff !important',
            borderColor: 'transparent !important',
            boxShadow: '0 8px 18px rgba(218, 41, 114, 0.28) !important',
            '&:hover': {
                background: '#C32266 !important',
            },
            '& .MuiListItemIcon-root': {
                color: '#ffffff !important',
            },
            '& .menu-label': {
                color: '#ffffff !important',
            },
        },
    },
    };
});

const childVariants = {
    hidden: { opacity: 0, x: -8 },
    visible: (i: number) => ({
        opacity: 1,
        x: 0,
        transition: {
            delay: i * 0.03,
            duration: 0.2,
            ease: 'easeOut',
        },
    }),
    exit: { opacity: 0, x: -6, transition: { duration: 0.12 } },
};

const SidebarCategory: FC<SidebarCategoryProps> = ({
    group,
    dense,
    onMenuClick,
    sidebarOpen,
    translate,
    nested = false,
}) => {
    const [open, setOpen] = useState(false);
    const classes = useStyles();

    const title = group.titleKey
        ? translate(group.titleKey, { smart_count: 2 })
        : group.title || '';

    const children = useMemo(
        () => sortLinks(group.children, group.sort, translate),
        [group.children, group.sort, translate]
    );

    return (
        <div className={`${nested ? classes.nestedRoot : classes.categoryRoot} sidebar-category`}>
            <button
                type="button"
                className={`${classes.parentButton} ${nested ? classes.nestedButton : ''} ${open ? classes.parentButtonOpen : ''} sidebar-parent`}
                onClick={() => setOpen((prev) => !prev)}
                aria-expanded={open}
            >
                <span className={`${classes.parentIcon} sidebar-parent-icon`}>
                    <grid.icon />
                </span>
                <span className={`${classes.parentLabel} menu-label`}>{title}</span>
                <motion.span
                    className={`${classes.chevron} sidebar-parent-chevron`}
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.22 }}
                >
                    <ExpandMoreIcon />
                </motion.span>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        key={`${group.id}-children`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.24, ease: 'easeInOut' }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className={`${classes.tree} sidebar-tree`}>
                            {children.map((item: SidebarChild, index: number) => (
                                <motion.div
                                    key={item.type === 'group' ? item.id : item.to}
                                    className={classes.treeItem}
                                    custom={index}
                                    variants={childVariants}
                                    initial="hidden"
                                    animate="visible"
                                    exit="exit"
                                >
                                    {item.type === 'group' ? (
                                        <SidebarCategory
                                            group={item}
                                            dense={dense}
                                            onMenuClick={onMenuClick}
                                            sidebarOpen={sidebarOpen}
                                            translate={translate}
                                            nested
                                        />
                                    ) : (
                                    <MenuItemLink
                                        to={item.to}
                                        primaryText={
                                            <span className="menu-label">
                                                {resolveLabel(item, translate)}
                                            </span>
                                        }
                                        leftIcon={<grid.icon />}
                                        sidebarIsOpen={sidebarOpen}
                                        dense={dense}
                                        onClick={onMenuClick}
                                        className={`${classes.childLink} sidebar-child-link`}
                                    />
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const SubMenu: FC<SubMenuProps> = ({ onMenuClick, dense, groups }) => {
    const translate = useTranslate();
    const sidebarOpen = useSelector((state: AppState) => state.admin.ui.sidebarOpen);

    return (
        <>
            {groups.map((group) => (
                <SidebarCategory
                    key={group.id}
                    group={group}
                    dense={dense}
                    onMenuClick={onMenuClick}
                    sidebarOpen={sidebarOpen}
                    translate={translate}
                />
            ))}
        </>
    );
};

export default SubMenu;
